import { useRef } from 'react';
import CyberLunaWindow from '../base/CyberLunaWindow';
import styles from './TikTokShelf.module.css';
import content from '../../data/content.json';

const { tiktoks } = content.home;

/**
 * TikTokShelf — horizontally scrollable row of vertical TikTok embeds.
 *
 * Each video is a phone-shaped XP window around TikTok's keyless iframe player
 * (tiktok.com/embed/v2/{id}). Unlike the YouTube shelf there's no thumbnail
 * facade — TikTok's thumbnail URLs are signed and expire — so iframes render
 * directly with loading="lazy", fetching only once scrolled into view.
 *
 * Videos live in content.json under home.tiktoks.
 */
const TikTokShelf = () => {
  const trackRef = useRef(null);

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild;
    const step = (card ? card.offsetWidth : 300) + 28;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  return (
    <section className={styles.shelf}>
      <h2 className={styles.title}>{tiktoks.title}</h2>
      <p className={styles.subtitle}>
        {tiktoks.subtitle}
        {' — '}
        <a
          href={tiktoks.handle_url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.handleLink}
        >
          {tiktoks.handle}
        </a>
      </p>

      <div className={styles.trackWrap}>
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowLeft}`}
          onClick={() => scrollByCard(-1)}
          aria-label="Scroll to previous video"
        >
          ‹
        </button>

        <div className={styles.track} ref={trackRef}>
          {tiktoks.videos.map((video) => (
            <div key={video.id} className={styles.card}>
              <CyberLunaWindow
                isStatic
                title={video.title}
                bodyClassName={styles.cardBody}
                maxBodyClassName={styles.modalBody}
              >
                <iframe
                  src={`https://www.tiktok.com/embed/v2/${video.id}`}
                  title={`TikTok: ${video.title}`}
                  className={styles.frame}
                  loading="lazy"
                  allow="encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </CyberLunaWindow>
            </div>
          ))}
        </div>

        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowRight}`}
          onClick={() => scrollByCard(1)}
          aria-label="Scroll to next video"
        >
          ›
        </button>
      </div>
    </section>
  );
};

export default TikTokShelf;
